import axios from '../utils/axios';

const API_URL = import.meta.env.VITE_APP_API_URL;
const authHeader = () => ({ Authorization: localStorage.getItem('jwt') });

// ── Consent Encryption Key ──────────────────────────────────────────

export interface ConsentKeyResponse {
    status: number;
    consentKey?: {
        id: number;
        investigationId: number;
        wrappedKey: string;
        nonce: string;
    };
    errorMessage?: string;
}

export const storeConsentKey = async (
    uuidInvestigation: string,
    wrappedKey: string,
    nonce: string
): Promise<ConsentKeyResponse> => {
    const response = await axios.post(
        `${API_URL}/researcher/investigation/${uuidInvestigation}/consent-key`,
        { wrappedKey, nonce },
        { headers: authHeader() }
    );
    return response.data;
};

export const getConsentKey = async (
    uuidInvestigation: string
): Promise<ConsentKeyResponse> => {
    const response = await axios.get(
        `${API_URL}/researcher/investigation/${uuidInvestigation}/consent-key`,
        { headers: authHeader() }
    );
    return response.data;
};

// ── Encrypted Consent Upload ────────────────────────────────────────

export interface EncryptedFileRecord {
    id: number;
    uuid: string;
    localId: string;
    nonce: string;
    s3Key: string;
    category: string;
    investigationId: number;
    patientInvestigationId: number | null;
    createdAt: string;
    // SKIN-43 — how to decode the ciphertext after decrypting. Optional so older
    // API responses (or any path not yet sending them) fall back to the legacy
    // plain-JPEG assumption in the decode utility.
    format?: string;
    compression?: string;
    // SKIN-233/236/237 — what the device said about the image, persisted as columns on
    // `encrypted_file`, which is now the authoritative store for all three. The blob
    // inside `survey_record.value` is only the historical record of the upload.
    //
    // All three are nullable rather than defaulted, and the client must preserve that
    // distinction: a legacy upload and a value the API rejected as out-of-range both
    // mean "no opinion", and rendering either as `0` or `false` would turn silence into
    // a claim about a patient's photograph.
    /** The device's auto-QC score, 0-100. Shown raw and uncoloured (SKIN-240). */
    qualityScore?: number | null;
    /** The authoritative identifiable flag — reflects any reviewer correction. */
    identifiable?: boolean | null;
    /** What the device originally called it, frozen; a correction never overwrites this. */
    autoIdentifiable?: boolean | null;
}

export interface UploadConsentResponse {
    status: number;
    result?: EncryptedFileRecord;
    errorMessage?: string;
}

export const uploadEncryptedConsent = async (
    uuidInvestigation: string,
    encryptedBlob: Blob,
    localId: string,
    nonce: string,
    uuidPatientInvestigation: string
): Promise<UploadConsentResponse> => {
    const formData = new FormData();
    formData.append('file', encryptedBlob, `${localId}.enc`);
    formData.append('localId', localId);
    formData.append('nonce', nonce);
    formData.append('category', 'consents');
    formData.append('uuidPatientInvestigation', uuidPatientInvestigation);

    const response = await axios.post(
        `${API_URL}/files/investigation/${uuidInvestigation}/images`,
        formData,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
};

// ── List Consents ───────────────────────────────────────────────────

export interface ListConsentsResponse {
    status: number;
    files?: EncryptedFileRecord[];
    errorMessage?: string;
}

export const listConsents = async (
    uuidInvestigation: string
): Promise<ListConsentsResponse> => {
    const response = await axios.get(
        `${API_URL}/researcher/investigation/${uuidInvestigation}/encrypted-files`,
        { headers: authHeader(), params: { category: 'consents' } }
    );
    return response.data;
};

// ── List Encrypted Files by LocalIds ────────────────────────────────

export const getEncryptedFilesByLocalIds = async (
    uuidInvestigation: string,
    localIds: string[]
): Promise<ListConsentsResponse> => {
    const response = await axios.get(
        `${API_URL}/researcher/investigation/${uuidInvestigation}/encrypted-files`,
        { headers: authHeader(), params: { localIds: localIds.join(',') } }
    );
    return response.data;
};

// ── Presigned Download ──────────────────────────────────────────────

export interface PresignedDownloadResponse {
    status: number;
    url?: string;
    errorMessage?: string;
}

export const getConsentDownloadUrl = async (
    uuidInvestigation: string,
    s3Key: string
): Promise<PresignedDownloadResponse> => {
    const response = await axios.get(
        `${API_URL}/researcher/investigation/${uuidInvestigation}/files/presigned-download`,
        { headers: authHeader(), params: { s3Key } }
    );
    const data = response.data;
    // Normalize: API returns { status, result: { downloadUrl } }
    // but consumers expect { status, url }
    return {
        status: data.status,
        url: data.result?.downloadUrl || data.url,
        errorMessage: data.errorMessage,
    };
};
